/**
 * useEditCartItem Hook
 *
 * Shared save/cancel logic for editing cart items.
 * Currently supports Bilberry native cart. Octo booking editing is not yet implemented.
 *
 * This primitive abstracts the complexity of:
 * - Bilberry: Remove + Re-add pattern with cartAtom (✅ IMPLEMENTED)
 * - Octo: Cancel + Create pattern with API calls (❌ NOT YET IMPLEMENTED)
 */

import { useCallback } from 'react';
import { CartItem, ProductInstance } from '@repo/types';
import { cartAtom } from 'src/state/cart/cartAtom';
import { removeFromCartEvent, createAddProductsToCartEvent } from 'src/state/cart/cart.reducer';
import { useBookingContext } from 'src/widgets/BookingContext';
import { setQuantities, setSelectedTimeSlot } from 'src/state/booking/bookingReducer';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

export type UseEditCartItemOptions = {
    cartItem: CartItem;
    onAfterSave?: () => void;
};

/**
 * Returns save and cancel functions for editing cart items
 */
export function useEditCartItem(options: UseEditCartItemOptions) {
    const { cartItem, onAfterSave } = options;
    const config = useConfigurations();
    const { quantities, selectedProducts, selectedTicketType, selectedTimeSlot, bookingDispatch } =
        useBookingContext();

    /**
     * Cancel edit - closes modal and resets state
     */
    const cancel = useCallback(() => {
        bookingDispatch(setSelectedTimeSlot(undefined));
        bookingDispatch(setQuantities([]));
        onAfterSave?.();
    }, [bookingDispatch, onAfterSave]);

    /**
     * Save edit for Bilberry native cart
     */
    const saveBilberry = useCallback(async () => {
        const selected = [
            ...(selectedProducts?.map((p) => ({ ...p, timeslots: [] })) ?? cartItem.products),
        ];

        // Validate we have products to work with
        if (selected.length === 0) {
            return;
        }

        const [{ product }] = selected;
        if (!product) return;
        if (product?.type === 'accommodation' && !selectedTicketType) return;

        // Handle timeslot products specially
        if (product?.type === 'timeslot') {
            if (!selectedTimeSlot?.products || selectedTimeSlot.products.length === 0) {
                return;
            }

            selected[0] = {
                ...selected[0],
                timeslots: selectedTimeSlot.products,
                start: selectedTimeSlot.products[0].start,
                end: selectedTimeSlot.products[selectedTimeSlot.products.length - 1].end,
            } as ProductInstance;
        }

        // Remove existing product and exchange with the new one
        await cartAtom.update(removeFromCartEvent(cartItem));
        await cartAtom.update(
            createAddProductsToCartEvent(
                selected,
                quantities,
                cartItem.disablePaymentPlans,
                cartItem.pkg,
                selectedTicketType,
            ),
        );

        // Cleanup
        bookingDispatch(setSelectedTimeSlot(undefined));
        bookingDispatch(setQuantities([]));
        onAfterSave?.();
    }, [
        cartItem,
        selectedProducts,
        quantities,
        selectedTicketType,
        selectedTimeSlot,
        bookingDispatch,
        onAfterSave,
    ]);

    /**
     * Save edit for Octo bookings
     *
     * NOT YET IMPLEMENTED
     *
     * Future implementation should:
     * 1. Cancel existing Octo booking via cancelOctoBooking()
     * 2. Build new Octo reservation request from quantities and selected products
     * 3. Create new Octo booking via createOctoBooking()
     * 4. Mark cart as dirty to trigger refresh
     * 5. Cleanup booking context state
     *
     * Requirements:
     * - CartItem needs octoBookingId field
     * - Need buildOctoReservationRequest() helper function
     * - Need to handle Octo API errors appropriately
     */
    const saveOcto = useCallback(async () => {
        throw new Error(
            'Editing Octo bookings is not currently supported. ' +
                'Please cancel the existing booking and create a new one instead.',
        );
    }, []);

    /**
     * Main save function - routes to correct backend
     */
    const save = useCallback(async () => {
        if (config.octo) {
            await saveOcto();
        } else {
            await saveBilberry();
        }
    }, [config.octo, saveOcto, saveBilberry]);

    return {
        save,
        cancel,
    };
}
